/**
 * Central Axios instance for the QR Code Generator API.
 *
 * All API modules import this client — never create raw axios instances elsewhere.
 *
 * Features:
 *  - Base URL from env (REACT_APP_API_BASE_URL) with local fallback
 *  - 30s timeout
 *  - Request interceptor: log + attach common headers if needed
 *  - Response interceptor: normalize all errors to plain Error objects with
 *    a human-readable `.message` and a numeric `.status`
 *  - Blob-aware error parsing: if the server sends a JSON error body with
 *    responseType:'blob', the interceptor reads it back before normalizing
 */
import axios from 'axios';
import { getAuthToken } from '../utils/authToken';

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  'https://unprovincially-unevaporated-venice.ngrok-free.dev';

const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    //'ngrok-skip-browser-warning': 'true',
  },
});

/* ── Request interceptor ── */
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
   
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(normalizeError(error))
);

/* ── Response interceptor ── */
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => Promise.reject(await normalizeError(error))
);

/**
 * Converts any axios error into a plain Error with:
 *  - `.message`  — human-readable string safe to show in the UI
 *  - `.status`   — HTTP status code (or undefined for network errors)
 *  - `.data`     — raw response body if available
 */
async function normalizeError(error) {
  let message = 'An unexpected error occurred. Please try again.';
  let status;
  let data;

  if (error.response) {
    status = error.response.status;
    data = error.response.data;

    // When responseType is 'blob' the data arrives as a Blob.
    // Try to decode it as JSON to extract the server message.
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const json = JSON.parse(text);
        data = json;
      } catch {
        // Not JSON — keep data as Blob, build a generic status message
      }
    }

    if (data && typeof data === 'object') {
      message = data.message || data.error || `Request failed (${status}).`;
    } else if (status === 401) {
      message = 'Unauthorized. Please check your credentials.';
    } else if (status === 403) {
      message = 'Access forbidden.';
    } else if (status === 404) {
      message = 'The requested resource was not found.';
    } else if (status >= 500) {
      message = 'Server error. Please try again later.';
    } else {
      message = `Request failed with status ${status}.`;
    }
  } else if (error.request) {
    // Request made but no response received (network / timeout)
    message =
      error.code === 'ECONNABORTED'
        ? 'Request timed out. Please check your connection and try again.'
        : 'Network error. Please check your internet connection.';
  }

  const normalized = new Error(message);
  normalized.status = status;
  normalized.data = data;
  normalized.isApiError = true;
  return normalized;
}

export default apiClient;
